var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import './polyfill.js';
import { html, LitElement } from 'lit';
import { state, customElement, query } from 'lit/decorators.js';
import { routes } from './router.js';
import { Router } from '@vaadin/router';
import { initializeAnalytics } from './analytics.js';
import { finishAuthenticationProcedure } from './initialization/sso.js';
import { initializeViewHeight } from './vhHack.js';
import { languageCodes } from './i18n.js';
import { defaultLanguage } from './unspecific/languageUtils.js';
import { normalizeURL, PARTIAL } from './urlHandling/urlNormalizer.js';
import './elements/element-header.js';
import './elements/element-top-notification.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { getBrowserCode } from './helpers/dom.js';
import { setCanonicalAndLanguageRelatedMetadata } from './initialization/canonicalURLHandler.js';
import { planLazyLoadingMapRelatedComponents } from './initialization/mapComponentsLazyLoading.js';
import { registerModalObserver } from './initialization/modalObserver.js';
import { loadFooter } from './initialization/footerLoader.js';
import './elements/element-notifications.js';
import { resolveDeployPrefix } from './initialization/deployPrefixResolver.js';
import { fetchAdminMessage, updateMaintenanceStatus, fetchSeason, } from './apis/api-common.js';
import { adminMessageLoaded$, adminMessageShown$, filterDirty$, modal$, season$, } from './stores/store-main.js';
import { classMap } from 'lit/directives/class-map.js';
import { isFilterDirty } from './helpers/filter.js';
import './elements/overlays/element-overlay.js';
import { resolvePartialUrl } from './initialization/partialURLHandler.js';
import { initializeObsoleteApiWatchdog, needsReload$, } from './initialization/obsoleteApi.js';
import './elements/element-bottom-sheet.js';
import { initializeOidcUserWithToken } from './helpers/oidcUser.js';
import { initializeUserWithCookie } from './helpers/cookieUser.js';
import { hasAuthCookie } from './helpers/tokenOrCookie.js';
import { toursFeedbackParamCheck } from './urlHandling/urlSearchParams.js';
import { SubscriptionsController } from './helpers/SubscriptionsController.js';
import { setupLayersPersistence } from './helpers/mapStateStorage.js';
main();
async function main() {
    initializeObsoleteApiWatchdog();
    initializeAnalytics();
    finishAuthenticationProcedure();
    const deployPrefix = resolveDeployPrefix();
    const initialURL = document.location.toString();
    const defaultSeasonCode = await fetchSeason();
    season$.next(defaultSeasonCode);
    const chosenFilters = localStorage.getItem('filters');
    filterDirty$.next(chosenFilters !== null ? isFilterDirty(JSON.parse(chosenFilters)) : false);
    const { normalizedURL, detectedLang, canonical, bag, code } = normalizeURL(initialURL, defaultLanguage, defaultSeasonCode, deployPrefix);
    if (normalizedURL && normalizedURL !== initialURL) {
        window.history.replaceState(null, undefined, normalizedURL);
    }
    if (canonical) {
        setCanonicalAndLanguageRelatedMetadata(normalizedURL, detectedLang, languageCodes, defaultSeasonCode, document);
    }
    else if (code === PARTIAL) {
        const url = await resolvePartialUrl(bag, normalizedURL);
        if (url) {
            location.href = url.toString();
            return;
        }
    }
    initializeViewHeight();
    if (hasAuthCookie()) {
        await initializeUserWithCookie();
    }
    else {
        await initializeOidcUserWithToken();
    }
    await updateMaintenanceStatus();
    const appIndexElement = document.createElement('app-index');
    const appEmbedIndexElement = document.createElement('app-embed-index');
    setupLayersPersistence();
    document.body.appendChild(bag.embed ? appEmbedIndexElement : appIndexElement);
}
let AppEmbedIndex = class AppEmbedIndex extends LitElement {
    constructor() {
        super();
        this.outletRef = createRef();
        this.notifRef = createRef();
        this.subscriptionController = new SubscriptionsController(this, () => [
            registerModalObserver(),
        ]);
        planLazyLoadingMapRelatedComponents();
    }
    render() {
        return html `
      <div
        id="main"
        class="overflow-overlay snap-y snap-proximity overflow-x-hidden"
      >
        <div ${ref(this.outletRef)}></div>
        <div
          class="notification-position w-notification flex justify-center fixed bottom-[20px] z-[70]"
        >
          <element-notifications ${ref(this.notifRef)}></element-notifications>
        </div>
      </div>
    `;
    }
    async firstUpdated() {
        const router = new Router();
        router.setOutlet(this.outletRef.value);
        this.addEventListener('alertnotif', ((evt) => {
            const notification = {
                type: evt.detail.type,
                message: evt.detail.message,
                html: evt.detail.html,
            };
            this.notifRef.value.sendNotification(notification);
        }));
        router.setRoutes(routes);
    }
    createRenderRoot() {
        return this;
    }
};
AppEmbedIndex = __decorate([
    customElement('app-embed-index')
], AppEmbedIndex);
let AppIndex = class AppIndex extends LitElement {
    constructor() {
        super();
        this.adminMessageShown = false;
        this.outletRef = createRef();
        this.notifRef = createRef();
        this.showToursFeedback = toursFeedbackParamCheck();
        this.subscriptionController = new SubscriptionsController(this, () => [
            registerModalObserver(),
            adminMessageShown$.subscribe((show) => (this.adminMessageShown = show)),
            needsReload$.subscribe((state) => {
                if (state.reload) {
                    this.notifRef.value.sendNotification({
                        type: 'error',
                        html: html `<div @click=${() => document.location.reload()}>
              Es ist ein Fehler aufgetreten, bitte lade die Seite neu
            </div>`,
                        message: '.',
                    }, 10000000000000);
                }
            }),
        ]);
        planLazyLoadingMapRelatedComponents();
    }
    render() {
        return html `
      <element-top-notification
        .message="${this.adminMessage}"
      ></element-top-notification>
      <element-header class="sticky top-0 z-[60] block"></element-header>
      <div
        id="main"
        class="overflow-overlay ${classMap({
            'h-screen-wo-header': !this.adminMessageShown,
            'h-screen-wo-header-message': this.adminMessageShown,
        })} snap-y snap-proximity overflow-x-hidden"
      >
        <div
          .hidden="${getBrowserCode() !== 'firefox'}"
          class="h-[0.01px] snap-start"
        ></div>
        <div ${ref(this.outletRef)}></div>
        <element-footer></element-footer>
        <div
          .hidden="${getBrowserCode() !== 'firefox'}"
          class="h-[1px] snap-end"
        ></div>
      </div>
      <element-place-details></element-place-details>
      <element-sightseeing-details></element-sightseeing-details>
      <element-travelreport-details></element-travelreport-details>
      <element-layers></element-layers>
      <element-poi-popup></element-poi-popup>
      <element-big-gallery></element-big-gallery>
      <element-overlay></element-overlay>
      <div
        class="notification-position w-notification flex justify-center fixed bottom-[20px] z-[70]"
      >
        <element-notifications ${ref(this.notifRef)}></element-notifications>
      </div>
      <element-bottom-sheet
        class="absolute bottom-0 w-full pointer-events-none"
        .scrollContainer="${this.scrollContainer}"
        .topNotificationEl="${this.topNotificationEl}"
      ></element-bottom-sheet>
    `;
    }
    async firstUpdated() {
        const router = new Router();
        router.setOutlet(this.outletRef.value);
        router.setRoutes(routes);
        loadFooter(this.outletRef.value);
        this.addEventListener('alertnotif', ((evt) => {
            const notification = {
                type: evt.detail.type,
                message: evt.detail.message,
                html: evt.detail.html,
            };
            this.notifRef.value.sendNotification(notification);
        }));
        const message = await fetchAdminMessage();
        this.adminMessage = message;
        adminMessageShown$.next(!!message);
        adminMessageLoaded$.next(true);
        if (this.showToursFeedback) {
            modal$.next({ type: 'contact' });
        }
    }
    createRenderRoot() {
        return this;
    }
};
__decorate([
    state()
], AppIndex.prototype, "adminMessage", void 0);
__decorate([
    state()
], AppIndex.prototype, "adminMessageShown", void 0);
__decorate([
    query('#main')
], AppIndex.prototype, "scrollContainer", void 0);
__decorate([
    query('element-top-notification')
], AppIndex.prototype, "topNotificationEl", void 0);
AppIndex = __decorate([
    customElement('app-index')
], AppIndex);
//# sourceMappingURL=app-index.js.map